import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Check, X, ShieldCheck, Scale,
  FileSearch, Gauge, Quote, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { StatCounter } from "@/components/shared/StatCounter";
import { CTABand } from "@/components/shared/CTABand";
import { useConsultation } from "@/components/forms/ConsultationDialog";
import {
  HERO, HERO_STATS, RISK_CARDS, WITHOUT_US, WITH_US, NEUTRALITY,
  STAKEHOLDERS, PRACTICES, TESTIMONIALS, PROCESS, INDUSTRIES, IMAGES,
} from "@/data/content";

const PROVIDERS = ["Cisco", "Microsoft", "AWS", "CrowdStrike", "Zscaler", "Verizon", "Fortinet", "Genesys", "Palo Alto", "Google Cloud", "Twilio", "Rackspace"];

/* ---------------------------------- HERO ---------------------------------- */
const Hero = () => {
  const { open } = useConsultation();
  return (
    <section className="relative overflow-hidden pt-28 sm:pt-36 lg:pt-44">
      {/* Background image + overlays */}
      <div className="absolute inset-0 -z-10">
        <img src={IMAGES.heroNetwork} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-grid opacity-60" />
      </div>

      <div className="section-x pb-20 sm:pb-28">
        <div className="container-tmt">
          <div className="max-w-4xl">
            <motion.span
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="kicker inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-1.5"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> {HERO.kicker}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
              className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl"
            >
              {HERO.headline}{" "}
              <span className="text-gradient">{HERO.headlineAccent}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
              className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground"
            >
              {HERO.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.28 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <Button variant="hero" size="xl" onClick={open}>
                Book a 25-Min Consultation <ArrowRight className="h-5 w-5" />
              </Button>
              <Button variant="heroGhost" size="xl" asChild>
                <Link to="/billing-breakthrough">Run a 5-Min Invoice Self-Check</Link>
              </Button>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border shadow-elegant lg:grid-cols-4"
          >
            {HERO_STATS.map((s) => (
              <div key={s.label} className="bg-card/80 p-6 backdrop-blur-sm sm:p-7">
                <StatCounter value={s.value} label={s.label} note={s.note} />
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ PROVIDER MARQUEE --------------------------- */
const ProviderBar = () => (
  <section className="border-y border-border bg-card/30 py-8">
    <div className="section-x">
      <div className="container-tmt">
        <p className="mb-6 text-center font-mono text-xs uppercase tracking-[0.28em] text-muted-foreground">
          Independent evaluations across 600+ vetted providers
        </p>
        <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
          <div className="marquee-track flex w-max items-center gap-12">
            {[...PROVIDERS, ...PROVIDERS].map((p, i) => (
              <span key={i} className="whitespace-nowrap font-display text-xl font-medium text-muted-foreground/70">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------- RISK CARDS ------------------------------ */
const RiskSection = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <SectionHeading
        kicker="Smart Teams Get Burned"
        title="The system isn't broken. It's working exactly as designed — against you."
        subtitle="Everyone offering you 'advice' has a stake in the outcome. Here's how good teams get quietly steered."
      />
      <RevealStagger className="mt-12 grid gap-6 md:grid-cols-3">
        {RISK_CARDS.map((c) => (
          <RevealItem key={c.title}>
            <div className="card-hairline relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 shadow-card transition-colors duration-300 hover:border-destructive/40">
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-destructive/30 bg-destructive/10 text-destructive">
                <X className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-semibold">{c.title}</h3>
              <div className="mt-4 space-y-3 text-sm">
                <p className="text-muted-foreground"><span className="font-mono text-xs uppercase tracking-wider text-foreground/70">You think: </span>{c.problem}</p>
                <p className="text-muted-foreground"><span className="font-mono text-xs uppercase tracking-wider text-destructive/90">Reality: </span>{c.reality}</p>
              </div>
              <div className="mt-auto pt-5">
                <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-sm text-foreground/90">
                  {c.consequence}
                </div>
              </div>
            </div>
          </RevealItem>
        ))}
      </RevealStagger>
    </div>
  </section>
);

/* ------------------------------- WITH/WITHOUT ----------------------------- */
const SplitMatrix = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <SectionHeading
        align="center"
        kicker="The Difference"
        title="Two ways to buy technology"
        subtitle="One path is built on vendor incentives. The other is built entirely around yours."
      />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="flex h-full flex-col rounded-2xl border border-border bg-card/60 p-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10 text-destructive"><X className="h-5 w-5" /></span>
              <h3 className="font-display text-xl font-semibold text-muted-foreground">Without an advocate</h3>
            </div>
            <ul className="space-y-4">
              {WITHOUT_US.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive/80" /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-primary/30 bg-gradient-surface p-8 shadow-glow">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
            <div className="relative mb-6 flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary"><Check className="h-5 w-5" /></span>
              <h3 className="font-display text-xl font-semibold">With TaylorMade</h3>
            </div>
            <ul className="relative space-y-4">
              {WITH_US.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground/90">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* -------------------------------- NEUTRALITY ------------------------------ */
const Neutrality = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionHeading kicker={NEUTRALITY.kicker} title={NEUTRALITY.title} subtitle={NEUTRALITY.body} />
          <Reveal delay={0.15}>
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-5 py-4">
              <Scale className="h-6 w-6 shrink-0 text-primary" />
              <p className="text-sm text-foreground/90">
                You wouldn’t hire your opponent’s lawyer. Why let vendors be your only tech advisor?
              </p>
            </div>
          </Reveal>
        </div>
        <RevealStagger className="grid gap-4">
          {NEUTRALITY.points.map((p) => (
            <RevealItem key={p.label}>
              <div className="card-hairline relative flex items-start gap-4 overflow-hidden rounded-2xl border border-border bg-card p-6 transition-colors duration-300 hover:border-primary/40">
                <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <h4 className="font-display text-lg font-semibold">{p.label}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </div>
  </section>
);

/* ------------------------------- STAKEHOLDERS ----------------------------- */
const Stakeholders = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <SectionHeading
        kicker="Value For Every Seat"
        title="One advocate. Aligned outcomes for the whole table."
        subtitle="Whether you sign the contracts, the checks, or the board minutes — we speak your language."
      />
      <RevealStagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {STAKEHOLDERS.map((s) => {
          const Icon = s.icon;
          return (
            <RevealItem key={s.role}>
              <div className="card-hairline relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
                <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="font-display text-lg font-semibold">{s.role}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.value}</p>
              </div>
            </RevealItem>
          );
        })}
      </RevealStagger>
    </div>
  </section>
);

/* --------------------------------- PROCESS -------------------------------- */
const ProcessPreview = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <SectionHeading
          kicker="How We Work"
          title="A full-cycle framework that removes the guesswork"
          subtitle="Six phases, one continuous relationship — from first discovery call to life-of-contract accountability."
        />
        <Reveal>
          <Button variant="outline" size="lg" asChild className="shrink-0">
            <Link to="/how-we-work">See the full process <ArrowUpRight className="h-4 w-4" /></Link>
          </Button>
        </Reveal>
      </div>
      <RevealStagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PROCESS.map((p) => (
          <RevealItem key={p.step}>
            <div className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-colors duration-300 hover:border-primary/40">
              <span className="font-mono text-sm text-primary">{p.step}</span>
              <h3 className="mt-3 font-display text-lg font-semibold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
            </div>
          </RevealItem>
        ))}
      </RevealStagger>
    </div>
  </section>
);

/* -------------------------------- SOLUTIONS ------------------------------- */
const SolutionsPreview = () => (
  <section className="relative section-x py-20 sm:py-28">
    <div className="container-tmt">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <SectionHeading
          kicker="Solutions"
          title="Full-stack sourcing. One independent advocate."
          subtitle="Six signature practices — each starting with an exhaustive, provider-agnostic assessment."
        />
        <Reveal>
          <Button variant="outline" size="lg" asChild className="shrink-0">
            <Link to="/solutions">Explore all practices <ArrowUpRight className="h-4 w-4" /></Link>
          </Button>
        </Reveal>
      </div>
      <RevealStagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {PRACTICES.map((p) => {
          const Icon = p.icon;
          return (
            <RevealItem key={p.slug}>
              <Link to="/solutions" className="card-hairline group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary">
                    <Icon className="h-6 w-6" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{p.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
                <span className="mt-4 inline-flex w-fit rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1 font-mono text-xs text-primary">
                  {p.tool}
                </span>
              </Link>
            </RevealItem>
          );
        })}
      </RevealStagger>
    </div>
  </section>
);

/* ------------------------------ ENTRY POINTS ------------------------------ */
const EntryPoints = () => {
  const { open } = useConsultation();
  return (
    <section className="section-x py-20 sm:py-28">
      <div className="container-tmt">
        <SectionHeading
          align="center"
          kicker="Low-Barrier Entry Points"
          title="Start with tangible value — no strings attached"
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="card-hairline relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-card">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent"><FileSearch className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display text-2xl font-semibold">The Billing Breakthrough™</h3>
              <p className="mt-3 text-muted-foreground">
                A free TEM audit of one month of invoices. We typically uncover 20–40% in direct waste, billing errors, and auto-renewed bloat — within one business day.
              </p>
              <div className="mt-auto pt-6">
                <Button variant="premium" size="lg" asChild className="w-full sm:w-auto">
                  <Link to="/billing-breakthrough">Upload for a Free Audit <ArrowRight className="h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-hairline relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-card">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary"><Gauge className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display text-2xl font-semibold">The ClarityMap™</h3>
              <p className="mt-3 text-muted-foreground">
                A custom market map of your options, benchmarked and independent — delivered in a typical 48-hour turnaround so you can move with confidence.
              </p>
              <div className="mt-auto pt-6">
                <Button variant="hero" size="lg" className="w-full sm:w-auto" onClick={open}>
                  Request Your ClarityMap <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ TESTIMONIALS ------------------------------ */
const Testimonials = () => (
  <section className="section-x py-20 sm:py-28">
    <div className="container-tmt">
      <SectionHeading
        kicker="Client Voices"
        title="A 96 NPS isn't an accident. It's the model working."
        subtitle="EY-Parthenon validated. Multi-decade retention. Here's what buyers say when the advice is finally on their side."
      />
      <RevealStagger className="mt-12 grid gap-6 lg:grid-cols-3">
        {TESTIMONIALS.map((t) => (
          <RevealItem key={t.org}>
            <figure className="flex h-full flex-col rounded-2xl border border-border bg-card p-8">
              <Quote className="h-8 w-8 text-primary/40" />
              <div className="mt-3 flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground/90">{`“${t.quote}”`}</blockquote>
              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="font-display font-semibold">{t.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{t.org}</div>
              </figcaption>
            </figure>
          </RevealItem>
        ))}
      </RevealStagger>

      <Reveal>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Deep specialization in</span>
          {INDUSTRIES.map((i) => (
            <span key={i} className="font-display text-sm font-medium text-foreground/80">{i}</span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <ProviderBar />
      <RiskSection />
      <SplitMatrix />
      <Neutrality />
      <Stakeholders />
      <ProcessPreview />
      <SolutionsPreview />
      <EntryPoints />
      <Testimonials />
      <CTABand />
    </>
  );
}
